@import 'core';

@import '@fortawesome/fontawesome-pro/scss/fontawesome.scss';
@import '@fortawesome/fontawesome-pro/scss/brands.scss';
@import '@fortawesome/fontawesome-pro/scss/light.scss';
@import '@fortawesome/fontawesome-pro/scss/regular.scss';
@import '@fortawesome/fontawesome-pro/scss/solid.scss';

@import 'components/ads-horizontal-tabs-tab/ads-horizontal-tabs-tab.scss';

/**
 * @prop --ads-horizontal-tabs-gradient-transparency: Top level transparency for border image gradient
 * @prop --ads-horizontal-tabs-navigation-background-color: Default top level background color
 * @prop --ads-horizontal-tabs-navigation-border-gradient: Top level border image gradient
 * @prop --ads-horizontal-tabs-tab-background-color: Default tab background color
 * @prop --ads-horizontal-tabs-tab-border-color: Default tab border color
 * @prop --ads-horizontal-tabs-tab-color: Default tab font color
 * @prop --ads-horizontal-tabs-tab-color-active: Tab font color on active
 * @prop --ads-horizontal-tabs-tab-color-hover: Tab font color on hover
 * @prop --ads-horizontal-tabs-tab-container-position: Tab container position behaviour
 * @prop --ads-horizontal-tabs-tab-container-top: Tab container vertical positioning
 * @prop --ads-horizontal-tabs-tab-content-background-color: Tab content color
 * @prop --ads-horizontal-tabs-underline-color: Tab border color on active
 * @prop --ads-horizontal-tabs-wrapper-left: Left element of wrapper
 * @prop --ads-horizontal-tabs-close-fill-color: Color of close icon
 * @prop --ads-horizontal-tabs-close-fill-color-hover: Hover color of close icon
 * @prop --ads-horizontal-tabs-navigation-border-bottom: Border bottom of navigation
 * @prop --ads-horizontal-tabs-tab-btn-border-bottom:  Border bottom of tab button
 */

$ads-horizontal-tabs-active-left: 0;
$ads-horizontal-tabs-active-width: 0;

:host {
  --ads-horizontal-tabs-active-left: #{$ads-horizontal-tabs-active-left};
  --ads-horizontal-tabs-active-width: #{$ads-horizontal-tabs-active-width};
  display: block;
  height: 100%;
  width: 100%;
}

:host([theme='default']) {
  @include ads-vars-component-horizontal-tabs-default;
  @include ads-vars-component-paragraph-default;
  @include ads-vars-component-heading-default;
}

:host([theme='dark']) {
  @include ads-vars-component-horizontal-tabs-dark;
  @include ads-vars-component-paragraph-dark;
  @include ads-vars-component-heading-dark;
}

:host([dir='ltr']) {
  @include ads-vars-component-horizontal-tabs-ltr;
}

:host([dir='rtl']) {
  @include ads-vars-component-horizontal-tabs-rtl;
}

.c-horizontal-tabs {
  background: var(--ads-horizontal-tabs-tab-content-background-color, $ads-horizontal-tabs-tab-content-background-color);
  font-family: var(--ads-font-family);

  &__content {
    background-color: var(--ads-horizontal-tabs-tab-content-background-color, $ads-horizontal-tabs-tab-content-background-color);
    position: relative; // Add this for positioning context
    min-height: 2px; // Prevent content jump during transitions
    // Isolate layout to prevent height from inactive tabs affecting container
    contain: layout style;
  }

  // Default styles for slotted content
  ::slotted(*) {
    opacity: 0;
    pointer-events: none;
    display: block !important;
    position: absolute; // Use absolute positioning instead of grid
    top: 0;
    left: 0;
    width: 100%;
    // Prevent inactive tabs from contributing to container height
    visibility: hidden;
    height: 0;
    overflow: hidden;
    transition: opacity 0s ease-in-out, visibility 0s ease-in-out, height 0s ease-in-out;
    // No transform by default to prevent stacking context issues with modals
  }

  // Active tab content
  ::slotted(.active) {
    opacity: 1;
    pointer-events: auto;
    position: relative; // Change to relative for proper stacking
    // Restore visibility and natural height for active tab
    visibility: visible;
    height: auto;
    overflow: visible;
    // No transform by default to prevent stacking context issues with modals
  }
  &__navigation {
    display: grid;
    gap: 2rem;
    grid-auto-columns: max-content;
    grid-auto-flow: column;
    padding-right: 1rem;
    position: relative;

    @include breakpoints-up() {
      padding-right: 0;
    }

    &::after {
      background-color: var(--ads-horizontal-tabs-underline-color, $ads-horizontal-tabs-underline-color);
      bottom: 0;
      content: '';
      height: 1px;
      left: var(--ads-horizontal-tabs-active-left, $ads-horizontal-tabs-active-left);
      position: absolute;
      transition: width 0.3s ease-in-out, left 0.3s ease-in-out;
      width: var(--ads-horizontal-tabs-active-width, $ads-horizontal-tabs-active-width);
    }

    &.no-transition::after {
      transition: none !important;
    }
  }

  &__tab-container {
    background-color: var(--ads-horizontal-tabs-navigation-background-color, $ads-horizontal-tabs-navigation-background-color);
    border: 0;
    border-bottom: var(--ads-horizontal-tabs-navigation-border-bottom , $ads-horizontal-tabs-navigation-border-bottom );
    border-image: var(--ads-horizontal-tabs-navigation-border-gradient, $ads-horizontal-tabs-navigation-border-gradient);
    border-image-slice: 1;
    display: flex;
    margin: 0 0 -1px 0;
    overflow-x: var(--ads-horizontal-tabs-container_overflow_x , $ads-horizontal-tabs-container_overflow_x);
    position: var(--ads-horizontal-tabs-tab-container-position, $ads-horizontal-tabs-tab-container-position);
    top: var(--ads-horizontal-tabs-tab-container-top, $ads-horizontal-tabs-tab-container-top);
    z-index: $layer-4;

    &::before {
      background: linear-gradient(
        var(--ads-horizontal-tabs-gradient-rotation, $ads-horizontal-tabs-gradient-rotation-ltr),
        var(--ads-horizontal-tabs-gradient-transparency, $ads-horizontal-tabs-gradient-transparency) 75%,
        var(--ads-horizontal-tabs-navigation-background-color, $ads-horizontal-tabs-navigation-background-color) 100%
      );
      content: '';
      display: inline-block;
      flex-shrink: 0;
      height: 40px;
      left: 0;
      pointer-events: none;
      position: sticky;
      right: 0;
      width: 100%;
      z-index: $layer-12;
    }
  }

  &__navigation-wrapper {
    left: var(--ads-horizontal-tabs-wrapper-left, $ads-horizontal-tabs-wrapper-left);
    position: absolute;
    padding-right: 30px;
  }
}

// Animation styles (when enableTabAnimation is true)
.c-horizontal-tabs--animated {
  .c-horizontal-tabs__content {
    overflow: hidden;
  }

  ::slotted(*) {
    // Prevent inactive tabs from contributing to container height
    visibility: hidden;
    height: 0;
    overflow: hidden;
    transform: translateY(100%); // Start fully below for animation
    opacity: 0;
    transition: transform 0.3s ease-in-out, opacity 0.2s ease-in-out, visibility 0s ease-in-out 0.3s, height 0s ease-in-out 0.3s;
  }

  ::slotted(.active) {
    // Restore visibility and natural height for active tab
    visibility: visible;
    height: auto;
    overflow: visible;
    transform: translateY(0); // Animate to normal position
    opacity: 1;
    transition-delay: 0.1s; // Slight delay for smoother entry
    transition: transform 0.3s ease-in-out, opacity 0.2s ease-in-out, visibility 0s ease-in-out, height 0s ease-in-out;
  }
}